import React from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle,
  Clock,
  Crosshair,
  Loader2,
  MapPin,
  Search,
} from 'lucide-react';
import Input from '../Input';
import Skeleton from '../Skeleton';
import styles from './MapPicker.module.scss';
import { useMapPicker } from './useMapPicker';

/* ────────────────────────────────────────────────────────────
 * MapPicker — Google Maps address picker.
 *
 * Search and map are one bound surface, never two modes: choosing a
 * suggestion flies the map, and moving the map rewrites the address.
 *
 * Three decisions worth knowing before you change this:
 *
 *   1. The pin is a CSS overlay fixed at the centre of the frame, not
 *      a google.maps.Marker. The user pans the map *under* it. That
 *      makes the drag target the entire map instead of a 40px icon —
 *      a large Fitts's-law win, and on touch your finger no longer
 *      covers the thing you are aiming at.
 *   2. `query` is local state. The form is only updated on a real
 *      commit (suggestion picked · map settled · my-location · a
 *      structured field edited). Emitting every keystroke is what
 *      previously let the saved address and the saved coordinates
 *      describe two different places.
 *   3. Zoom is set once per result — from the place's own viewport
 *      where possible — and never touched again while panning.
 *
 *   <MapPicker
 *     id="addnewstore-map"
 *     value={{ lat, lng, address, landmark, city, state, country, postalCode }}
 *     onChange={(loc) => setLocation(loc)}
 *     countries={['IN']}
 *   />
 * ──────────────────────────────────────────────────────────── */

const GEO_HINTS = {
  denied:
    'Location permission is blocked. Allow it in your browser settings, or search instead.',
  unavailable: 'Your location is unavailable right now. Search or drag the pin.',
};

const DETAIL_FIELDS = {
  landmark: { label: 'Area / Landmark', placeholder: 'e.g. near City Mall' },
  city: { label: 'City', placeholder: 'City' },
  state: { label: 'State', placeholder: 'State' },
  country: { label: 'Country', placeholder: 'Country' },
  postalCode: { label: 'Postal code', placeholder: 'Postal code' },
};

const MapPicker = ({
  id,
  value,
  onChange,
  searchPlaceholder,
  error,
  disabled,
  className,
  defaultCenter,
  defaultZoom,
  countries,
  region,
  language,
  showDetailFields,
  detailFields,
  detailNote,
}) => {
  const {
    mapNodeRef,
    status,
    loadError,
    query,
    listItems,
    showingRecents,
    activeIndex,
    setActiveIndex,
    open,
    searching,
    searchError,
    resolving,
    dragging,
    location,
    geoState,
    handleQueryChange,
    handleKeyDown,
    handleFocus,
    handleBlur,
    armBlurGuard,
    selectSuggestion,
    selectRecent,
    useMyLocation,
    setDetailField,
  } = useMapPicker({
    value,
    onChange,
    defaultCenter,
    defaultZoom,
    countries,
    region,
    language,
    disabled,
  });

  const listboxId = id ? `${id}-listbox` : 'mappicker-listbox';
  const showList = open && !disabled && listItems.length > 0;
  const secondaryLine = [
    location?.city,
    location?.state,
    location?.postalCode,
    location?.country,
  ]
    .filter(Boolean)
    .join(', ');

  if (status === 'error') {
    return (
      <div className={[styles.wrapper, className || ''].filter(Boolean).join(' ')}>
        <div className={styles.fatal} role="alert">
          <AlertTriangle size={20} strokeWidth={1.75} aria-hidden="true" />
          <div>
            <p className={styles.fatalTitle}>Map unavailable</p>
            <p className={styles.fatalBody}>{loadError?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={[styles.wrapper, className || ''].filter(Boolean).join(' ')}>
      {/* ── Search ─────────────────────────────────────────── */}
      <div className={styles.searchWrap}>
        <Input
          id={id}
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={searchPlaceholder}
          leftIcon={<Search size={16} strokeWidth={1.75} />}
          rightIcon={
            searching ? (
              <Loader2 size={16} strokeWidth={1.75} className={styles.spin} />
            ) : null
          }
          disabled={disabled || status === 'loading'}
          error={error || searchError}
          autoComplete="off"
          spellCheck="false"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showList && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
          }
        />

        {showList && (
          <ul className={styles.suggestions} role="listbox" id={listboxId}>
            {showingRecents && (
              <li className={styles.listHeading} role="presentation">
                Recent
              </li>
            )}
            {listItems.map((item, index) => {
              const active = index === activeIndex;
              const main = showingRecents
                ? item.primaryLine || item.address
                : item.mainText || item.text;
              const sub = showingRecents
                ? item.address
                : item.secondaryText;
              return (
                <li
                  key={showingRecents ? `${item.lat}-${item.lng}` : item.placeId}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={active}
                  className={[styles.suggestion, active ? styles.suggestionActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => setActiveIndex(index)}
                  onPointerDown={armBlurGuard}
                  onClick={() =>
                    showingRecents ? selectRecent(item) : selectSuggestion(item)
                  }
                >
                  <span className={styles.suggestionIcon} aria-hidden="true">
                    {showingRecents ? (
                      <Clock size={14} strokeWidth={1.75} />
                    ) : (
                      <MapPin size={14} strokeWidth={1.75} />
                    )}
                  </span>
                  <span className={styles.suggestionText}>
                    <span className={styles.suggestionMain}>{main}</span>
                    {sub && <span className={styles.suggestionSub}>{sub}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Map ────────────────────────────────────────────── */}
      <div className={styles.mapFrame}>
        {status === 'loading' && (
          <div className={styles.mapLoading} aria-hidden="true">
            <Skeleton width="100%" height="100%" radius="md" />
          </div>
        )}

        <div
          ref={mapNodeRef}
          className={styles.map}
          role="application"
          aria-label="Map — pan to position the pin, or press the locate button"
        />

        {/* Centre pin. Purely decorative: the map itself is the control. */}
        <div
          className={[styles.pin, dragging ? styles.pinLifted : '']
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          <MapPin size={32} strokeWidth={2} className={styles.pinIcon} />
          <span className={styles.pinShadow} />
        </div>

        <button
          type="button"
          className={styles.locateBtn}
          onClick={useMyLocation}
          disabled={disabled || status !== 'ready' || geoState === 'locating'}
          aria-label="Use my current location"
          title="Use my current location"
        >
          {geoState === 'locating' ? (
            <Loader2 size={16} strokeWidth={1.75} className={styles.spin} />
          ) : (
            <Crosshair size={16} strokeWidth={1.75} />
          )}
        </button>

        <p className={styles.mapHint} aria-hidden="true">
          Drag the map to move the pin
        </p>
      </div>

      {GEO_HINTS[geoState] && (
        <p className={styles.geoHint} role="status">
          {GEO_HINTS[geoState]}
        </p>
      )}

      {/* ── Confirmation card ──────────────────────────────── */}
      <div className={styles.confirm} aria-live="polite">
        <span className={styles.confirmIcon} aria-hidden="true">
          <MapPin size={16} strokeWidth={1.75} />
        </span>
        <div className={styles.confirmText}>
          {resolving ? (
            <>
              <Skeleton width="60%" height="0.9rem" radius="sm" />
              <Skeleton width="40%" height="0.75rem" radius="sm" />
            </>
          ) : location ? (
            <>
              <p className={styles.confirmPrimary}>
                {location.primaryLine || location.address || 'Unnamed location'}
              </p>
              <p className={styles.confirmSecondary}>{secondaryLine || '—'}</p>
            </>
          ) : (
            <p className={styles.confirmEmpty}>
              No location selected yet — search above, or drag the map.
            </p>
          )}
        </div>

        {location && !resolving && (
          <span className={styles.coords}>
            <span className={styles.coordValue}>{location.lat}</span>
            <span className={styles.coordSep}>,</span>
            <span className={styles.coordValue}>{location.lng}</span>
          </span>
        )}
      </div>

      {location?.isApproximate && !resolving && (
        <p className={styles.approxHint} role="status">
          <AlertTriangle size={13} strokeWidth={1.75} aria-hidden="true" />
          This point is approximate. Drag the map to place it exactly.
        </p>
      )}

      {/* ── Structured fields ──────────────────────────────── */}
      {showDetailFields && (
        <div className={styles.details}>
          <p className={styles.detailsNote}>{detailNote}</p>
          <div className={styles.detailsGrid}>
            {detailFields.map((key) => {
              const field = DETAIL_FIELDS[key];
              if (!field) return null;
              return (
                <label
                  key={key}
                  className={styles.detailField}
                  htmlFor={`${id || 'mappicker'}-${key}`}
                >
                  <span className={styles.detailLabel}>{field.label}</span>
                  <Input
                    id={`${id || 'mappicker'}-${key}`}
                    value={location?.[key] || ''}
                    onChange={setDetailField(key)}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    autoComplete="off"
                  />
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

MapPicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.string,
    landmark: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  defaultCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  defaultZoom: PropTypes.number,
  /** CLDR region codes the autocomplete is limited to, e.g. ['IN']. */
  countries: PropTypes.arrayOf(PropTypes.string),
  region: PropTypes.string,
  language: PropTypes.string,
  showDetailFields: PropTypes.bool,
  /** Which structured fields to render. Show only what the consuming
   *  form actually persists — an editable input that is silently
   *  dropped on save is worse than no input at all. */
  detailFields: PropTypes.arrayOf(
    PropTypes.oneOf(['landmark', 'city', 'state', 'country', 'postalCode'])
  ),
  detailNote: PropTypes.string,
};

MapPicker.defaultProps = {
  id: undefined,
  value: null,
  searchPlaceholder: 'Search for a place or address…',
  error: '',
  disabled: false,
  className: '',
  defaultCenter: { lat: 10.8505, lng: 76.2711 },
  defaultZoom: 17,
  countries: undefined,
  region: undefined,
  language: undefined,
  showDetailFields: true,
  detailFields: ['landmark', 'city', 'state', 'country', 'postalCode'],
  detailNote:
    'Filled in from the map — correct anything the map got wrong before saving.',
};

export default MapPicker;
